'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React from 'react';

import { Button } from '@ui/components/common/button';
import { ActivityMobileCard } from '@ui/components/features/activity/activityMobileCard';
import activityPlaceholder from '@website/assets/images/activity_placeholder.webp';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { useModeSwitcher } from '@website/hooks/useModeSwitcher';
import { Activity } from '@website/services/activities.service';

interface ActivityDrawerContentProps {
  activity: Activity | null;
  onPrev?: () => void;
  onNext?: () => void;
  currentIndex?: number;
  totalCount?: number;
}

export function ActivityDrawerContent({
  activity,
  onPrev,
  onNext,
  currentIndex,
  totalCount
}: ActivityDrawerContentProps) {
  const t = useTranslations();
  const router = useRouter();
  const locale = useLocale();
  const { data: user } = useCurrentUser();

  // Pre-compute returnTo for the mentor-mode-switch path so the user lands
  // back on the slot picker after flipping to learner. The switcher hook
  // tolerates a missing user (open() becomes a no-op) and an empty
  // returnTo, so it's safe to instantiate even when activity is still null.
  const slotsReturnTo = activity ? `/${locale}/activity/${activity.id}/slots` : undefined;
  const slotsModeSwitcher = useModeSwitcher(user ?? undefined, { returnTo: slotsReturnTo });

  const handleViewActivity = () => {
    if (activity) {
      router.push(`/${locale}/activity/${activity.id}`);
    }
  };

  const handleViewSlots = () => {
    if (!activity) return;
    if (user?.isProfessional) {
      // Mentors get the same explicit "switch to learner first?" confirm
      // dialog as on the activity detail page - no silent role flips.
      slotsModeSwitcher.open();
      return;
    }
    router.push(`/${locale}/activity/${activity.id}/slots`);
  };

  if (!activity) {
    return (
      <div className="flex justify-center items-center h-full p-6">
        <p>{t('Common.loading')}</p>
      </div>
    );
  }

  const showNavigation = totalCount !== undefined && totalCount > 1;

  return (
    <div className="flex flex-col h-full">
      {/* Group navigation bar */}
      {showNavigation && (
        <div className="flex items-center justify-between px-2 py-2 border-b border-neutral-200">
          <button
            onClick={onPrev}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-neutral-100 text-neutral-600"
            aria-label="Previous activity"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="text-sm font-medium text-neutral-600">
            {(currentIndex ?? 0) + 1} / {totalCount}
          </span>
          <button
            onClick={onNext}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-neutral-100 text-neutral-600"
            aria-label="Next activity"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      {/* Activity Mobile Card */}
      <div className="md:hidden">
        {(() => {
          // Mirror ListingCard: for a single-kind activity the card's location row
          // renders the caller-computed label via `atHomeBadge`, so derive the
          // kind-correct text (and isOnline) instead of always passing "At Home".
          // A >=2-kind activity uses the locationKindLabels chip path instead.
          const onlyKind = activity.locationKinds?.length === 1 ? activity.locationKinds[0] : undefined;
          const isOnlineOnly = onlyKind === 'online';
          const singleKindBadge = isOnlineOnly
            ? t('ActivityCard.online')
            : activity.isAtHome
              ? t('ActivityCard.atHome')
              : t('ActivityCard.atVenue');
          return (
            <ActivityMobileCard
              title={activity.title}
              professionalName={activity.professionalName}
              imageUrl={activity.imageUrl}
              isAtHome={activity.isAtHome}
              isOnline={isOnlineOnly}
              locationKinds={activity.locationKinds}
              locationKindLabels={{
                venue: t('ActivityCard.atVenue'),
                at_home: t('ActivityCard.atHome'),
                online: t('ActivityCard.online')
              }}
              startingPrice={activity.startingPrice}
              height="h-[250px]"
              labels={{
                atHomeBadge: singleKindBadge,
                priceLabel: t('ActivityCard.priceLabel'),
                priceFrom: t('ActivityDrawer.priceFrom'),
                contactForPrice: t('Common.contactForPrice')
              }}
              fallbackImage={activityPlaceholder.src}
              onClick={handleViewActivity}
            />
          );
        })()}
      </div>
      {/* Action Buttons */}
      <div
        className="pt-4 mt-auto bg-white border-t border-neutral-200 flex space-x-3"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <Button
          variant="secondary"
          label={t('ActivityCard.viewActivityButton')}
          onClick={handleViewActivity}
          className="flex-1"
        />
        {/* Not bookable yet: no slot picker to send them to. */}
        {activity.availability !== 'none' && (
          <Button
            variant="primary"
            label={t('ActivityCard.availableSlotsButton')}
            onClick={handleViewSlots}
            className="flex-1"
            disabled={slotsModeSwitcher.isPending}
          />
        )}
      </div>
      {slotsModeSwitcher.dialogs}
    </div>
  );
}
